import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, CheckCircle, XCircle, Lock, Link2, Fingerprint, ChevronDown, ChevronRight, ArrowRight, ShieldAlert } from 'lucide-react';
import type { ToolComponentProps } from './index';

interface ChainCertificate {
  subject: string;
  subjectFull: string;
  issuer: string;
  issuerOrg: string;
  validFrom: string;
  validTo: string;
  daysRemaining: number;
  serialNumber: string;
  fingerprint256: string;
  isCA: boolean;
  isSelfSigned: boolean;
}

interface CertificateInfo {
  subject: string;
  subjectFull: string;
  issuer: string;
  issuerOrg: string;
  validFrom: string;
  validTo: string;
  daysRemaining: number;
  serialNumber: string;
  fingerprint: string;
  fingerprint256: string;
  subjectAltNames: string[];
  protocol: string;
}

interface CertificateResult {
  host: string;
  port: number;
  valid: boolean;
  authorized: boolean;
  nameMatch: boolean;
  error: string | null;
  certificate: CertificateInfo | null;
  chain: ChainCertificate[];
  chainInfo: {
    length: number;
    hasIntermediate: boolean;
    hasRoot: boolean;
    isComplete: boolean;
  };
}

// In production, nginx serves both frontend and API from same origin
const API_BASE = '';

export function SSLChecker({ initialValue, initialPort }: ToolComponentProps) {
  const [input, setInput] = useState(initialValue || '');
  const [port, setPort] = useState(initialPort || '443');
  const [result, setResult] = useState<CertificateResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedChainCerts, setExpandedChainCerts] = useState<Set<number>>(new Set([0]));
  const hasAutoTriggered = useRef(false);

  const handleCheck = useCallback(async () => {
    let host = input.trim();
    if (!host) {
      setError('Please enter a hostname');
      return;
    }

    // Strip protocol if provided
    host = host.replace(/^https?:\/\//, '').split('/')[0];
    
    // Parse port number
    const portNum = parseInt(port, 10) || 443;
    if (portNum < 1 || portNum > 65535) {
      setError('Port must be between 1 and 65535');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);
    setExpandedChainCerts(new Set([0]));

    try {
      const url = portNum === 443 
        ? `${API_BASE}/api/ssl/${encodeURIComponent(host)}`
        : `${API_BASE}/api/ssl/${encodeURIComponent(host)}?port=${portNum}`;
      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Certificate check failed');
        return;
      }

      setResult(data);

      // Update browser URL for sharing
      const params = new URLSearchParams();
      params.set('host', host);
      if (portNum !== 443) params.set('port', portNum.toString());
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    } catch (err) {
      setError('Failed to connect to the server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [input, port]);

  // Auto-trigger check when initialValue is provided via URL
  useEffect(() => {
    if (initialValue && !hasAutoTriggered.current) {
      hasAutoTriggered.current = true;
      handleCheck();
    }
  }, [initialValue, handleCheck]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCheck();
    }
  };

  const toggleChainCert = (index: number) => {
    setExpandedChainCerts((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const getStatusColor = (days: number) => {
    if (days <= 0) return 'text-red-500 dark:text-red-400';
    if (days <= 30) return 'text-red-500 dark:text-red-400';
    return 'text-[var(--accent)]';
  };

  const getDaysLabel = (days: number) => {
    if (days <= 0) return 'Expired';
    if (days === 1) return '1 day left';
    return `${days} days left`;
  };

  const getCertTypeLabel = (cert: ChainCertificate, index: number) => {
    if (index === 0) return 'Leaf Certificate';
    if (cert.isSelfSigned) return 'Root CA';
    return 'Intermediate CA';
  };

  const getCertTypeBadge = (cert: ChainCertificate, index: number) => {
    if (index === 0) {
      return <span className="px-2 py-0.5 text-xs rounded bg-blue-500/20 text-blue-400">Leaf</span>;
    }
    if (cert.isSelfSigned) {
      return <span className="px-2 py-0.5 text-xs rounded bg-purple-500/20 text-purple-400">Root CA</span>;
    }
    return <span className="px-2 py-0.5 text-xs rounded bg-amber-500/20 text-amber-400">Intermediate</span>;
  };

  const renderOutput = () => {
    if (error) {
      return (
        <div className="text-red-400 p-4 bg-red-500/10 rounded-lg">
          <span className="font-semibold">Error:</span> {error}
        </div>
      );
    }

    if (!result) {
      return (
        <div className="text-[var(--text-muted)] text-center py-8">
          Enter a hostname or IP address above and click "Check Certificate" to analyze the SSL/TLS certificate chain.
        </div>
      );
    }

    const cert = result.certificate;
    
    // Determine specific issues
    const hasNameMismatch = cert && result.nameMatch === false;
    const hasChainIssue = cert && !result.chainInfo.isComplete;
    const isExpired = cert && cert.daysRemaining <= 0;
    const isNotTrusted = cert && !result.authorized;

    return (
      <div className="space-y-6">
        {/* Status Summary */}
        <div className={`flex items-center gap-4 p-4 rounded-lg ${result.valid ? 'bg-[var(--accent-dim)]' : 'bg-red-500/10'}`}>
          {result.valid ? (
            <CheckCircle className="w-10 h-10 text-[var(--accent)]" />
          ) : (
            <XCircle className="w-10 h-10 text-red-400" />
          )}
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className={`font-semibold text-lg ${result.valid ? 'text-[var(--accent)]' : 'text-red-400'}`}>
                {result.valid ? 'Certificate Valid & Trusted' : result.certificate ? 'Certificate Issues Detected' : 'Connection Failed'}
              </span>
              {cert && (
                <span className={`px-2 py-0.5 text-sm font-medium rounded ${getStatusColor(cert.daysRemaining)} bg-[var(--bg-tertiary)]`}>
                  {getDaysLabel(cert.daysRemaining)}
                </span>
              )}
            </div>
            <div className="text-sm text-[var(--text-muted)] mt-1">
              {result.host}:{result.port}
              {cert && (
                <span className="ml-3 inline-flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  {cert.protocol}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Issue Details */}
        {!result.valid && cert && (
          <div className="space-y-2">
            {hasNameMismatch && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <div>
                  <div className="font-medium">Name Mismatch</div>
                  <div className="text-sm text-red-400/80">
                    The certificate is not valid for "{result.host}". Check the Subject Alternative Names below.
                  </div>
                </div>
              </div>
            )}
            {isNotTrusted && !hasNameMismatch && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <div>
                  <div className="font-medium">Certificate Not Trusted</div>
                  <div className="text-sm text-red-400/80">
                    The certificate chain is not trusted by this system. It may be self-signed or issued by an untrusted CA.
                  </div>
                </div>
              </div>
            )}
            {hasChainIssue && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <div>
                  <div className="font-medium">Incomplete Certificate Chain</div>
                  <div className="text-sm text-red-400/80">
                    The server is not sending all intermediate certificates. This may cause trust issues in some browsers.
                  </div>
                </div>
              </div>
            )}
            {isExpired && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <div>
                  <div className="font-medium">Certificate Expired</div>
                  <div className="text-sm text-red-400/80">
                    This certificate expired {Math.abs(cert.daysRemaining)} days ago.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Certificate Details */}
        {cert && (
          <div>
            <h3 className="text-[var(--accent)] font-semibold mb-3 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              Server Certificate
            </h3>
            <div className="space-y-2 text-sm bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <span className="text-[var(--text-muted)]">Subject:</span>
                <span className="break-all font-medium">{cert.subject}</span>
              </div>
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <span className="text-[var(--text-muted)]">Issuer:</span>
                <span className="break-all">{cert.issuerOrg}</span>
              </div>
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <span className="text-[var(--text-muted)]">Valid From:</span>
                <span>{new Date(cert.validFrom).toLocaleDateString()}</span>
              </div>
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <span className="text-[var(--text-muted)]">Valid To:</span>
                <span className={getStatusColor(cert.daysRemaining)}>
                  {new Date(cert.validTo).toLocaleDateString()}
                </span>
              </div>
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <span className="text-[var(--text-muted)]">Trusted:</span>
                <span className={result.authorized ? 'text-[var(--accent)]' : 'text-red-400'}>
                  {result.authorized ? '✓ Yes' : '✗ No'}
                </span>
              </div>
              {cert.fingerprint256 && (
                <div className="grid grid-cols-[120px_1fr] gap-2 pt-2 border-t border-[var(--border-color)]">
                  <span className="text-[var(--text-muted)]">SHA-256:</span>
                  <span className="font-mono text-xs break-all text-[var(--text-muted)]">{cert.fingerprint256}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Certificate Chain */}
        {result.chain.length > 0 && (
          <div>
            <h3 className="text-[var(--accent)] font-semibold mb-3 flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              Certificate Chain ({result.chainInfo.length} certificate{result.chainInfo.length !== 1 ? 's' : ''})
            </h3>

            {/* Chain Info Summary */}
            <div className="flex flex-wrap gap-2 mb-4">
              <span className={`px-2 py-1 text-xs rounded ${result.chainInfo.hasIntermediate ? 'bg-[var(--accent-dim)] text-[var(--accent)]' : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)]'}`}>
                {result.chainInfo.hasIntermediate ? '✓' : '✗'} Intermediate CA
              </span>
              <span className={`px-2 py-1 text-xs rounded ${result.chainInfo.hasRoot ? 'bg-[var(--accent-dim)] text-[var(--accent)]' : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)]'}`}>
                {result.chainInfo.hasRoot ? '✓' : '✗'} Root CA
              </span>
              {!result.chainInfo.hasRoot && (
                <span className="px-2 py-1 text-xs rounded bg-orange-500/20 text-orange-500 dark:text-orange-400">
                  Root CA not sent (normal - trusted by OS)
                </span>
              )}
            </div>

            {/* Chain Certificates */}
            <div className="space-y-2">
              {result.chain.map((chainCert, index) => (
                <div key={index} className="border border-[var(--border-color)] rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleChainCert(index)}
                    className="w-full flex items-center gap-3 p-3 bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors text-left"
                  >
                    {expandedChainCerts.has(index) ? (
                      <ChevronDown className="w-4 h-4 text-[var(--text-muted)] shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-[var(--text-muted)] shrink-0" />
                    )}
                    <span className="text-[var(--text-muted)] text-sm w-6">#{index + 1}</span>
                    {getCertTypeBadge(chainCert, index)}
                    <span className="flex-1 font-medium truncate">{chainCert.subject}</span>
                    <span className={`text-xs ${getStatusColor(chainCert.daysRemaining)}`}>
                      {getDaysLabel(chainCert.daysRemaining)}
                    </span>
                  </button>

                  {expandedChainCerts.has(index) && (
                    <div className="p-4 bg-[var(--bg-primary)] border-t border-[var(--border-color)] space-y-2 text-sm">
                      <div className="grid grid-cols-[100px_1fr] gap-2">
                        <span className="text-[var(--text-muted)]">Type:</span>
                        <span>{getCertTypeLabel(chainCert, index)}</span>
                      </div>
                      <div className="grid grid-cols-[100px_1fr] gap-2">
                        <span className="text-[var(--text-muted)]">Subject:</span>
                        <span className="break-all font-mono text-xs">{chainCert.subjectFull}</span>
                      </div>
                      <div className="grid grid-cols-[100px_1fr] gap-2">
                        <span className="text-[var(--text-muted)]">Issuer:</span>
                        <span className="break-all font-mono text-xs">{chainCert.issuer}</span>
                      </div>
                      <div className="grid grid-cols-[100px_1fr] gap-2">
                        <span className="text-[var(--text-muted)]">Valid:</span>
                        <span>
                          {new Date(chainCert.validFrom).toLocaleDateString()} → {new Date(chainCert.validTo).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="grid grid-cols-[100px_1fr] gap-2">
                        <span className="text-[var(--text-muted)]">Serial:</span>
                        <span className="font-mono text-xs break-all">{chainCert.serialNumber}</span>
                      </div>
                      {chainCert.fingerprint256 && (
                        <div className="grid grid-cols-[100px_1fr] gap-2">
                          <span className="text-[var(--text-muted)]">SHA-256:</span>
                          <span className="font-mono text-xs break-all text-[var(--text-muted)]">{chainCert.fingerprint256}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Subject Alternative Names */}
        {cert && cert.subjectAltNames && cert.subjectAltNames.length > 0 && (
          <div>
            <h3 className="text-[var(--accent)] font-semibold mb-3 flex items-center gap-2">
              <Fingerprint className="w-4 h-4" />
              Subject Alternative Names ({cert.subjectAltNames.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {cert.subjectAltNames.slice(0, 15).map((san, index) => (
                <span
                  key={index}
                  className="px-2 py-1 rounded bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-xs font-mono"
                >
                  {san}
                </span>
              ))}
              {cert.subjectAltNames.length > 15 && (
                <span className="px-2 py-1 text-xs text-[var(--text-muted)]">
                  +{cert.subjectAltNames.length - 15} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Security Headers Link */}
        {result.valid && (
          <Link
            to={`/tools/security-headers?host=${encodeURIComponent(result.host)}`}
            className="flex items-center justify-between p-4 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-secondary)] hover:border-[var(--accent)] hover:bg-[var(--accent-dim)] transition-colors group"
          >
            <div className="flex items-center gap-3">
              <ShieldAlert className="w-5 h-5 text-[var(--accent)]" />
              <div>
                <div className="text-[var(--text-primary)] font-medium">
                  Check your Security Headers
                </div>
                <div className="text-sm text-[var(--text-muted)]">
                  Check CSP, HSTS, X-Frame-Options and more security headers
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-[var(--accent)]">
              <span className="text-sm hidden sm:inline">Scan Security Headers</span>
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        )}

      </div>
    );
  };

  return (
    <div className="space-y-6" onKeyDown={handleKeyDown}>
      {/* Input Section */}
      <div className="space-y-2">
        <label className="block text-xs sm:text-sm font-medium text-[var(--text-primary)]">
          Hostname or IP Address
        </label>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <input
            type="text"
            className="flex-1 min-w-0 px-3 sm:px-4 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent font-mono text-sm"
            placeholder="example.com or 192.168.1.1"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            spellCheck={false}
          />
          <div className="flex gap-2 sm:gap-3">
            <input
              type="text"
              className="w-20 px-3 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent font-mono text-center text-sm"
              placeholder="443"
              value={port}
              onChange={(e) => setPort(e.target.value.replace(/\D/g, '').slice(0, 5))}
              spellCheck={false}
            />
            <button
              type="button"
              onClick={handleCheck}
              disabled={isLoading || !input.trim()}
              className="btn-primary flex items-center justify-center gap-2 flex-1 sm:flex-none sm:px-6 whitespace-nowrap"
            >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Checking...
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4" />
                <span className="hidden sm:inline">Check Certificate</span>
                <span className="sm:hidden">Check</span>
              </>
            )}
            </button>
          </div>
        </div>
      </div>

      {/* Output Section */}
      <div className="space-y-2">
        <label className="block text-xs sm:text-sm font-medium text-[var(--text-primary)]">
          Certificate Chain Analysis
        </label>
        <div className="output-panel">
          {renderOutput()}
        </div>
      </div>

      <div className="text-xs text-[var(--text-muted)]">
        <span className="text-[var(--accent)]">Note:</span> This tool connects directly to the server via TLS to retrieve the complete certificate chain.
      </div>
    </div>
  );
}
